import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Play, Check, Undo2, Zap, Mail, Phone } from "lucide-react";

// Check for reduced motion preference
const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  return prefersReducedMotion;
};

// Grip dots like the real app
const GripDots = ({ className = "" }) => (
  <div className={`grid grid-cols-2 gap-[2px] ${className}`}>
    <div className="w-[2.5px] h-[2.5px] rounded-full bg-current" />
    <div className="w-[2.5px] h-[2.5px] rounded-full bg-current" />
    <div className="w-[2.5px] h-[2.5px] rounded-full bg-current" />
    <div className="w-[2.5px] h-[2.5px] rounded-full bg-current" />
    <div className="w-[2.5px] h-[2.5px] rounded-full bg-current" />
    <div className="w-[2.5px] h-[2.5px] rounded-full bg-current" />
  </div>
);

// Category icon component matching the app
const CategoryIcon = ({ type, size = 11 }) => {
  const iconProps = { size, strokeWidth: 1.5, className: "text-stone-400" };
  switch (type) {
    case 'email': return <Mail {...iconProps} />;
    case 'phone': return <Phone {...iconProps} />;
    default: return null;
  }
};

// Critical toggle (asterisk) like the app
const CriticalToggle = ({ isActive }) => (
  <span 
    className={`text-[15px] font-bold leading-none select-none ${isActive ? 'text-amber-400' : 'text-stone-400'}`}
    style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
  >
    *
  </span>
);

// Highlighter effect for important tasks
const HighlighterText = ({ children, isActive, isDone }) => {
  if (!isActive) return <span>{children}</span>;
  
  return (
    <span
      className="relative"
      style={{ 
        display: 'inline',
        background: isDone 
          ? 'linear-gradient(to bottom, transparent 5%, rgba(254, 243, 199, 0.25) 12%, rgba(254, 243, 199, 0.28) 88%, transparent 95%)'
          : 'linear-gradient(to bottom, transparent 3%, rgba(253, 224, 71, 0.28) 10%, rgba(253, 224, 71, 0.38) 50%, rgba(253, 224, 71, 0.32) 90%, transparent 97%)',
        padding: '0.1em 0.25em',
        margin: '0 -0.25em',
        borderRadius: '2px',
      }}
    >
      {children}
    </span>
  );
};

// Task card matching the real app styling - with depth system
const TaskCard = ({ 
  text, 
  notes, 
  category, 
  isImportant, 
  isDone, 
  completedDate,
  showPlayButton,
  showUndoButton,
  showCheckButton,
  threadBorderColor
}) => {
  // Match app's elevation system exactly
  const cardShadow = isDone 
    ? '0 2px 5px rgba(0, 0, 0, 0.06), 0 6px 16px rgba(0, 0, 0, 0.07), 0 12px 32px rgba(0, 0, 0, 0.05)' 
    : '0 2px 6px rgba(0, 0, 0, 0.07), 0 8px 20px rgba(0, 0, 0, 0.09), 0 16px 40px rgba(0, 0, 0, 0.07)';
  
  return (
    <div 
      className={`
        ${isDone ? 'bg-[#FDF9F3]' : 'bg-white'} 
        rounded-xl ${isDone ? 'p-2' : 'p-2.5'} 
        transition-all relative overflow-hidden
      `}
      style={{ 
        boxShadow: cardShadow,
        borderLeftWidth: threadBorderColor ? '3px' : undefined,
        borderLeftColor: threadBorderColor,
      }}
    >
      {/* Card surface depth - top highlight */}
      {!isDone && (
        <div 
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={{ 
            background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.3) 20%, transparent 50%)',
          }}
        />
      )}
      {/* Card surface depth - bottom edge */}
      {!isDone && (
        <div 
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={{ 
            background: 'linear-gradient(0deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.01) 15%, transparent 35%)',
          }}
        />
      )}
      
      {/* Top row: controls - matching actual app layout */}
      <div className="flex items-center justify-between mb-1 relative z-10">
        {/* Left: Asterisk + Drag handle + Play/Undo */}
        <div className="flex items-center gap-1.5">
          <div className="p-1">
            <CriticalToggle isActive={isImportant} />
          </div>
          <div className="p-1 text-[#A39E97]">
            <GripDots />
          </div>
          {showPlayButton && (
            <div className="p-1 text-[#847D76]">
              <Play size={13} />
            </div>
          )}
          {showUndoButton && (
            <div className="p-1 text-[#847D76]">
              <Undo2 size={13} />
            </div>
          )}
        </div>
        
        {/* Right: Check button only */}
        <div className="flex items-center">
          {showCheckButton && (
            <div className="p-1.5 text-green-500">
              <Check size={16} strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>
      
      {/* Task content */}
      <div className="relative z-10">
        <p className={`text-[14px] leading-relaxed break-words font-normal ${isDone ? 'text-[#9CA3AF] line-through decoration-[#a1a1a1]' : 'text-[#0F0F11]'}`}>
          <HighlighterText isActive={isImportant && !isDone} isDone={isDone}>
            {text}
          </HighlighterText>
          {category && category !== 'action' && (
            <span className="ml-1.5 opacity-50 inline-block align-middle">
              <CategoryIcon type={category} />
            </span>
          )}
        </p>
        {notes && (
          <p className="text-[#A1A1AA] text-xs mt-0.5 break-words">{notes}</p>
        )}
        {isDone && isImportant && (
          <div className="flex items-center gap-1 mt-0.5 text-[10px] text-amber-500/80">
            <span>*</span>
            <span>Important · Completed</span>
          </div>
        )}
        {isDone && completedDate && (
          <div className="flex justify-end mt-0.5">
            <span className="text-[10px] text-[#a3a3a3] opacity-70">
              {completedDate}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Subsection header for grouped tasks
const SubsectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5 mb-1.5 mt-3 first:mt-0">
    <Icon size={12} strokeWidth={1.5} className="text-stone-400" />
    <span className="text-[10px] font-medium uppercase tracking-wider text-stone-400">
      {label}
    </span>
  </div>
);

// Column component matching the app - with depth system
const BoardColumn = ({ title, children, isDone = false }) => {
  const isDoingColumn = title.toLowerCase() === 'doing';
  const isDoneColumn = title.toLowerCase() === 'done';
  
  // Column background tones for subtle separation
  const getColumnBg = () => {
    if (isDone) return '#FAF9F6';
    if (isDoingColumn) return '#F6FAFA';
    return '#FAFAF8';
  };
  
  // Header color based on column
  const getHeaderColor = () => {
    if (isDoneColumn) return 'text-green-500';
    if (isDoingColumn) return 'text-[#F58227]';
    return 'text-stone-500';
  };
  
  return (
    <div 
      className="flex-1 min-w-0 rounded-xl p-3 relative overflow-hidden"
      style={{ 
        backgroundColor: getColumnBg(),
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.06), 0 12px 32px rgba(0, 0, 0, 0.05)'
      }}
    >
      {/* Column surface depth - top highlight */}
      <div 
        className="absolute inset-0 pointer-events-none rounded-xl"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, transparent 50%)' }}
      />
      {/* Column surface depth - bottom edge */}
      <div 
        className="absolute inset-0 pointer-events-none rounded-xl"
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.025) 0%, transparent 40%)' }}
      />
      
      {/* Column header */}
      <div className="flex items-center mb-3 relative z-10">
        <GripDots className="text-stone-400 mr-2" />
        <h3 className={`text-[11px] font-semibold uppercase tracking-wider ${getHeaderColor()}`}>
          {title.toLowerCase() === 'to do' ? (
            <>
              To <span className="text-green-500">Do</span>
            </>
          ) : (
            title
          )}
        </h3>
      </div>
      
      {/* Cards - Done column gets slightly more spacing */}
      <div className={`${isDoneColumn ? 'space-y-3' : 'space-y-2.5'} relative z-10`}>
        {children}
      </div>
    </div>
  );
};

export const ProductPreviewSection = () => {
  const reducedMotion = useReducedMotion();

  return (
    <section 
      id="product"
      className="py-16 md:py-24 px-6 md:px-12 bg-gradient-to-b from-white to-[#FDFBF7]"
      data-testid="product-preview-section"
      aria-label="Product experience previews"
    >
      {/* Torn notebook paper quote */}
      <div className="flex justify-center mb-16 md:mb-20">
        <div className="relative max-w-xl transform rotate-[-0.7deg]">
          <div
            className="relative"
            style={{
              background: 'linear-gradient(to bottom, #FEF9E7 0%, #FDF6E3 100%)',
              padding: '28px 32px 32px 48px',
              boxShadow: '2px 3px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            {/* Top torn edge */}
            <div 
              className="absolute -top-[6px] left-0 right-0 h-[12px] overflow-hidden"
              style={{ background: 'transparent' }}
            >
              <svg width="100%" height="12" preserveAspectRatio="none" viewBox="0 0 400 12">
                <path 
                  d="M0,8 Q10,2 20,7 T40,5 T60,8 T80,4 T100,7 T120,5 T140,8 T160,4 T180,7 T200,5 T220,8 T240,4 T260,7 T280,5 T300,8 T320,4 T340,7 T360,5 T380,8 T400,6 L400,12 L0,12 Z" 
                  fill="#FEF9E7"
                />
              </svg>
            </div>
            {/* Bottom torn edge */}
            <div 
              className="absolute -bottom-[6px] left-0 right-0 h-[12px] overflow-hidden"
              style={{ background: 'transparent' }}
            >
              <svg width="100%" height="12" preserveAspectRatio="none" viewBox="0 0 400 12">
                <path 
                  d="M0,0 L400,0 L400,4 Q390,10 380,5 T360,7 T340,4 T320,8 T300,5 T280,7 T260,4 T240,8 T220,5 T200,7 T180,4 T160,8 T140,5 T120,7 T100,4 T80,8 T60,5 T40,7 T20,4 T0,6 Z" 
                  fill="#FDF6E3"
                />
              </svg>
            </div>
            {/* Notebook lines */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 27px, #E8DCCA 27px, #E8DCCA 28px)',
                backgroundPosition: '0 20px',
                opacity: 0.5,
              }}
            />
            {/* Red margin line */}
            <div 
              className="absolute top-0 bottom-0 left-10 w-[1px] pointer-events-none"
              style={{ backgroundColor: 'rgba(220, 100, 100, 0.3)' }}
            />
            {/* Text */}
            <p className="font-handwritten text-[1.4rem] md:text-[1.6rem] text-[#4A4540] leading-[1.85] relative z-10">
              "I kept picking up my phone to do something important, and three minutes later I'd be watching mall fights with no memory of why I unlocked it."
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto">
        {/* Section intro */}
        <motion.div
          initial={{ opacity: reducedMotion ? 1 : 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: reducedMotion ? 0 : 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#0CBCBF] mb-3">
            The experience
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold text-[#27272A] tracking-tight">
            Clarity. Not complexity.
          </h2>
        </motion.div>

        {/* Board Preview - matching real app layout with depth */}
        <motion.div
          initial={{ opacity: reducedMotion ? 1 : 0, y: reducedMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reducedMotion ? 0 : 0.6 }}
          className="rounded-2xl p-4 md:p-6 relative overflow-hidden"
          style={{ 
            background: 'linear-gradient(135deg, #F0FAF9 0%, #F9F8F6 50%, #FDFBF7 100%)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.08), 0 24px 48px rgba(0, 0, 0, 0.06)' 
          }}
        >
          {/* Board surface depth */}
          <div 
            className="absolute inset-0 pointer-events-none rounded-2xl"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, transparent 30%)' }}
          />
          
          {/* Board header */}
          <div className="flex items-center justify-between mb-4 px-1 relative z-10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#F58227]" />
              <span className="text-sm font-semibold text-stone-600">My Focus Board</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm">🔥</span>
              <span className="text-sm font-semibold text-orange-500">12</span>
            </div>
          </div>
          
          {/* Three columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 relative z-10">
            {/* TO DO Column */}
            <BoardColumn title="To Do">
              <TaskCard 
                text="Ask CP about Friday"
                category=""
                isImportant={false}
                showPlayButton={true}
                showCheckButton={true}
              />
              <TaskCard 
                text="Draft intro"
                isImportant={true}
                showPlayButton={true}
                showCheckButton={true}
              />
              <TaskCard 
                text="Wash soccer jerseys"
                notes="links in docs"
                isImportant={false}
                showPlayButton={true}
                showCheckButton={true}
              />
            </BoardColumn>
            
            {/* DOING Column with subsections */}
            <BoardColumn title="Doing">
              {/* Action subsection */}
              <SubsectionHeader icon={Zap} label="Action" />
              <TaskCard 
                text="Plan weekly rhythm"
                isImportant={true}
                threadBorderColor="#3DC4D0"
                showUndoButton={true}
                showCheckButton={true}
              />
              
              {/* Emails subsection */}
              <SubsectionHeader icon={Mail} label="Emails" />
              <TaskCard 
                text="Email team update"
                category="email"
                isImportant={false}
                threadBorderColor="#6DD9D0"
                showUndoButton={true}
                showCheckButton={true}
              />
              
              {/* Calls subsection */}
              <SubsectionHeader icon={Phone} label="Calls" />
              <TaskCard 
                text="Call Faizon"
                category="phone"
                isImportant={false}
                threadBorderColor="#A8D5C8"
                showUndoButton={true}
                showCheckButton={true}
              />
            </BoardColumn>
            
            {/* DONE Column */}
            <BoardColumn title="Done" isDone={true}>
              <TaskCard 
                text="Check calendar"
                isDone={true}
                isImportant={true}
                completedDate="Today"
              />
              <TaskCard 
                text="Change bank password"
                isDone={true}
                isImportant={false}
                completedDate="Yesterday"
              />
              <TaskCard 
                text="Review project notes"
                isDone={true}
                isImportant={false}
                completedDate="Jun 12"
              />
            </BoardColumn>
          </div>
          
          {/* Bottom caption */}
          <div className="mt-6 text-center">
            <p className="text-base md:text-lg font-medium text-[#5B6370] tracking-tight">
              Replace scattered notes and stay in flow.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductPreviewSection;
